import React, { useEffect, useMemo, useState } from 'react';
import { api } from '../../api/client';
import type { User, Workday } from '../../api/types';
import { useToast } from '../../components/Toast';
import { Modal } from '../../components/Modal';
import { AsyncButton } from '../../components/AsyncButton';
import { fmtDateISO, fmtHM, fmtTime, todayISO, ymdLocal } from '../../utils/time';
import { computeDurationsClient } from '../../utils/workday';

type AbsenceLabel = 'Ferie/permesso' | 'Ferie' | 'Permesso';

type DayKind = 'WORKING' | 'SATURDAY' | 'SUNDAY' | 'HOLIDAY';

type PayslipDay = {
  ymd: string;
  date: Date;
  inMonth: boolean;
  kind: DayKind;
  holidayName: string | null;
  isWorkingDay: boolean;
  workday: Workday | null;

  expectedMin: number;
  workedMin: number;
  breakMin: number;
  ordinaryMin: number;
  absenceMin: number;
  overtimeMin: number;
};

function isoYYYYMMDD(d: Date) {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');

  return `${yyyy}-${mm}-${dd}`;
}

function startOfMonthFromValue(monthValue: string) {
  const [yyyy, mm] = monthValue.split('-').map(Number);

  return new Date(yyyy, mm - 1, 1);
}

function addMonthsISO(monthValue: string, months = 1) {
  const d = startOfMonthFromValue(monthValue);

  d.setMonth(d.getMonth() + months);

  return isoYYYYMMDD(d);
}

function startOfWeekMonday(d: Date) {
  const x = new Date(d);
  const day = (x.getDay() + 6) % 7;

  x.setDate(x.getDate() - day);
  x.setHours(0, 0, 0, 0);

  return x;
}

function endOfWeekMonday(d: Date) {
  const x = new Date(d);
  const day = (x.getDay() + 6) % 7;

  x.setDate(x.getDate() + (6 - day));
  x.setHours(0, 0, 0, 0);

  return x;
}

function monthGridDays(monthValue: string) {
  const first = startOfMonthFromValue(monthValue);
  const last = new Date(first.getFullYear(), first.getMonth() + 1, 0);

  const start = startOfWeekMonday(first);
  const end = endOfWeekMonday(last);

  const days: Date[] = [];
  const cur = new Date(start);

  while (cur.getTime() <= end.getTime()) {
    days.push(new Date(cur));
    cur.setDate(cur.getDate() + 1);
  }

  return days;
}

function currentMonthValue() {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');

  return `${yyyy}-${mm}`;
}

function minutesFromHours(hours: number) {
  return Math.round(hours * 60);
}

function safeNumber(value: string | number, fallback: number) {
  const n = Number(value);

  return Number.isFinite(n) ? n : fallback;
}

function fmtDecimalHours(totalMin: number) {
  return (totalMin / 60).toLocaleString('it-IT', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
}

function easterSunday(year: number) {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;

  return new Date(year, month - 1, day);
}

function addDays(d: Date, days: number) {
  const x = new Date(d);

  x.setDate(x.getDate() + days);

  return x;
}

function italianHolidays(year: number) {
  const map = new Map<string, string>();

  map.set(`${year}-01-01`, 'Capodanno');
  map.set(`${year}-01-06`, 'Epifania');
  map.set(`${year}-04-25`, 'Liberazione');
  map.set(`${year}-05-01`, 'Festa del lavoro');
  map.set(`${year}-06-02`, 'Festa della Repubblica');
  map.set(`${year}-08-15`, 'Ferragosto');
  map.set(`${year}-11-01`, 'Ognissanti');
  map.set(`${year}-12-08`, 'Immacolata');
  map.set(`${year}-12-25`, 'Natale');
  map.set(`${year}-12-26`, 'Santo Stefano');

  const pasquetta = addDays(easterSunday(year), 1);
  map.set(isoYYYYMMDD(pasquetta), 'Pasquetta');

  return map;
}

function parseExtraHolidays(value: string) {
  const map = new Map<string, string>();

  value
    .split(',')
    .map((x) => x.trim())
    .filter(Boolean)
    .forEach((ymd) => {
      if (/^\d{4}-\d{2}-\d{2}$/.test(ymd)) {
        map.set(ymd, 'Festivo extra');
      }
    });

  return map;
}

function buildMonthHolidayMap(monthValue: string, extraHolidays: string) {
  const first = startOfMonthFromValue(monthValue);
  const year = first.getFullYear();

  const map = new Map<string, string>();

  for (const [k, v] of italianHolidays(year)) {
    map.set(k, v);
  }

  for (const [k, v] of parseExtraHolidays(extraHolidays)) {
    map.set(k, v);
  }

  return map;
}

function dayKind(date: Date, holidayName: string | null): DayKind {
  const day = date.getDay();

  if (holidayName) return 'HOLIDAY';
  if (day === 6) return 'SATURDAY';
  if (day === 0) return 'SUNDAY';

  return 'WORKING';
}

function kindLabel(kind: DayKind, holidayName: string | null) {
  if (kind === 'HOLIDAY') return holidayName || 'Festivo';
  if (kind === 'SATURDAY') return 'Sabato';
  if (kind === 'SUNDAY') return 'Domenica';

  return 'Lavorativo';
}

function kindBadgeStyle(kind: DayKind) {
  if (kind === 'WORKING') {
    return {
      background: 'rgba(255,255,255,.08)',
      border: '1px solid var(--border)'
    };
  }

  if (kind === 'HOLIDAY') {
    return {
      background: 'rgba(255, 180, 0, .14)',
      border: '1px solid rgba(255, 180, 0, .35)'
    };
  }

  return {
    background: 'rgba(255,255,255,.04)',
    border: '1px solid var(--border)'
  };
}

function compareYMD(a: string, b: string) {
  if (a < b) return -1;
  if (a > b) return 1;

  return 0;
}

export function AdminCedolinoPage() {
  const { push } = useToast();

  const [users, setUsers] = useState<User[]>([]);
  const [userId, setUserId] = useState('');
  const [month, setMonth] = useState(() => currentMonthValue());
  const [contractMode, setContractMode] = useState<'7' | '8' | 'custom'>('8');
  const [customContractHours, setCustomContractHours] = useState('8');
  const [absenceLabel, setAbsenceLabel] = useState<AbsenceLabel>('Ferie/permesso');
  const [extraHolidays, setExtraHolidays] = useState('');

  const [items, setItems] = useState<Workday[]>([]);
  const [selectedDay, setSelectedDay] = useState<PayslipDay | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);

  const today = todayISO();

  const contractHours = useMemo(() => {
    if (contractMode === '7') return 7;
    if (contractMode === '8') return 8;

    const n = safeNumber(customContractHours, 8);

    return Math.max(0, n);
  }, [contractMode, customContractHours]);

  const contractMin = useMemo(() => {
    return minutesFromHours(contractHours);
  }, [contractHours]);

  const selectedUser = useMemo(() => {
    return users.find((u) => u._id === userId) || null;
  }, [users, userId]);

  const monthRange = useMemo(() => {
    return {
      from: `${month}-01`,
      to: addMonthsISO(month, 1)
    };
  }, [month]);

  async function loadUsers() {
    const r = await api.get<{ users: User[] }>('/api/admin/users');

    setUsers(r.users || []);

    if (!userId && r.users?.[0]) {
      setUserId(r.users[0]._id);
    }
  }

  async function loadWorkdays() {
    if (!userId) return;

    try {
      const r = await api.get<{ items: Workday[] }>(
        `/api/admin/users/${userId}/workdays?from=${monthRange.from}&to=${monthRange.to}`
      );

      setItems(r.items || []);
    } catch (e: any) {
      push({
        title: 'Errore cedolino',
        body: e?.data?.message || e?.data?.error || e?.message
      });
    }
  }

  useEffect(() => {
    loadUsers().catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadWorkdays().catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, month]);

  const workdayByDate = useMemo(() => {
    const map = new Map<string, Workday>();

    for (const w of items) {
      map.set(ymdLocal(w.work_date), w);
    }

    return map;
  }, [items]);

  const holidayMap = useMemo(() => {
    return buildMonthHolidayMap(month, extraHolidays);
  }, [month, extraHolidays]);

  const days = useMemo<PayslipDay[]>(() => {
    const grid = monthGridDays(month);
    const [year, monthNumber] = month.split('-').map(Number);

    return grid.map((date) => {
      const ymd = isoYYYYMMDD(date);
      const inMonth = date.getFullYear() === year && date.getMonth() + 1 === monthNumber;
      const holidayName = holidayMap.get(ymd) || null;
      const kind = dayKind(date, holidayName);
      const isWorkingDay = kind === 'WORKING';

      const workday = workdayByDate.get(ymd) || null;
      const dur = workday ? computeDurationsClient(workday, new Date()) : null;

      const workedMin = dur?.netMin || 0;
      const breakMin = dur?.breakMin || 0;

      let expectedMin = 0;
      let ordinaryMin = 0;
      let absenceMin = 0;
      let overtimeMin = 0;

      if (inMonth && isWorkingDay) {
        expectedMin = contractMin;
        ordinaryMin = Math.min(workedMin, contractMin);
        absenceMin = Math.max(0, contractMin - workedMin);
        overtimeMin = Math.max(0, workedMin - contractMin);
      }

      if (inMonth && !isWorkingDay && workedMin > 0) {
        expectedMin = 0;
        ordinaryMin = 0;
        absenceMin = 0;
        overtimeMin = workedMin;
      }

      return {
        ymd,
        date,
        inMonth,
        kind,
        holidayName,
        isWorkingDay,
        workday,
        expectedMin,
        workedMin,
        breakMin,
        ordinaryMin,
        absenceMin,
        overtimeMin
      };
    });
  }, [month, holidayMap, workdayByDate, contractMin]);

  const monthDaysOnly = useMemo(() => {
    return days.filter((d) => d.inMonth);
  }, [days]);

  const totals = useMemo(() => {
    const workingDays = monthDaysOnly.filter((d) => d.isWorkingDay).length;
    const holidays = monthDaysOnly.filter((d) => d.kind === 'HOLIDAY').length;
    const saturdaySunday = monthDaysOnly.filter((d) => d.kind === 'SATURDAY' || d.kind === 'SUNDAY').length;

    const expectedMin = monthDaysOnly.reduce((sum, d) => sum + d.expectedMin, 0);
    const workedMin = monthDaysOnly.reduce((sum, d) => sum + d.workedMin, 0);
    const breakMin = monthDaysOnly.reduce((sum, d) => sum + d.breakMin, 0);
    const absenceMin = monthDaysOnly.reduce((sum, d) => sum + d.absenceMin, 0);
    const overtimeMin = monthDaysOnly.reduce((sum, d) => sum + d.overtimeMin, 0);

    const absentFullDays = monthDaysOnly.filter((d) => d.isWorkingDay && !d.workday).length;
    const partialAbsenceDays = monthDaysOnly.filter((d) => d.isWorkingDay && d.workday && d.absenceMin > 0).length;

    return {
      workingDays,
      holidays,
      saturdaySunday,
      expectedMin,
      workedMin,
      breakMin,
      absenceMin,
      overtimeMin,
      absentFullDays,
      partialAbsenceDays
    };
  }, [monthDaysOnly]);

  function openDay(d: PayslipDay) {
    if (!d.inMonth) return;

    setSelectedDay(d);
    setDetailOpen(true);
  }

  function previousMonth() {
    const d = startOfMonthFromValue(month);

    d.setMonth(d.getMonth() - 1);

    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');

    setMonth(`${yyyy}-${mm}`);
  }

  function nextMonth() {
    const d = startOfMonthFromValue(month);

    d.setMonth(d.getMonth() + 1);

    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');

    setMonth(`${yyyy}-${mm}`);
  }

  const orderedMonthDays = useMemo(() => {
    return [...monthDaysOnly].sort((a, b) => compareYMD(a.ymd, b.ymd));
  }, [monthDaysOnly]);

  return (
    <div className="grid" style={{ gap: 16 }}>
      <div className="card">
        <div className="spread" style={{ gap: 12, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <div>
            <div className="h2" style={{ marginTop: 0, marginBottom: 4 }}>
              Cedolino
            </div>

            <div className="small">
              Calcolo mensile ore, pause, {absenceLabel.toLowerCase()} e straordinari.
              Sabato, domenica e festivi non generano ore da contratto.
            </div>
          </div>

          <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
            <button className="btn btnGhost" type="button" onClick={previousMonth}>
              ← Mese prec.
            </button>

            <button className="btn btnGhost" type="button" onClick={nextMonth}>
              Mese succ. →
            </button>
          </div>
        </div>

        <div
          className="grid"
          style={{
            gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))',
            gap: 10,
            marginTop: 14
          }}
        >
          <div>
            <div className="small">
              Dipendente
            </div>

            <select
              className="input"
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
            >
              {users.map((u) => (
                <option key={u._id} value={u._id}>
                  {u.first_name} {u.last_name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <div className="small">
              Mese
            </div>

            <input
              className="input"
              type="month"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
            />
          </div>

          <div>
            <div className="small">
              Ore da contratto al giorno
            </div>

            <select
              className="input"
              value={contractMode}
              onChange={(e) => setContractMode(e.target.value as '7' | '8' | 'custom')}
            >
              <option value="7">7 ore</option>
              <option value="8">8 ore</option>
              <option value="custom">Personalizzato</option>
            </select>
          </div>

          {contractMode === 'custom' ? (
            <div>
              <div className="small">
                Ore personalizzate
              </div>

              <input
                className="input"
                type="number"
                min="0"
                step="0.25"
                value={customContractHours}
                onChange={(e) => setCustomContractHours(e.target.value)}
              />
            </div>
          ) : null}

          <div>
            <div className="small">
              Assenza da mostrare come
            </div>

            <select
              className="input"
              value={absenceLabel}
              onChange={(e) => setAbsenceLabel(e.target.value as AbsenceLabel)}
            >
              <option value="Ferie/permesso">Ferie/permesso</option>
              <option value="Ferie">Ferie</option>
              <option value="Permesso">Permesso</option>
            </select>
          </div>

          <div>
            <div className="small">
              Festivi extra
            </div>

            <input
              className="input"
              placeholder="es. 2026-09-19, 2026-12-24"
              value={extraHolidays}
              onChange={(e) => setExtraHolidays(e.target.value)}
            />
          </div>
        </div>

        <div className="spread" style={{ marginTop: 12, gap: 10, flexWrap: 'wrap' }}>
          <AsyncButton
            className="btn btnPrimary"
            onClickAsync={loadWorkdays}
            busyText="Calcolo…"
            disabled={!userId}
          >
            Calcola cedolino
          </AsyncButton>

          <div className="small">
            Dipendente selezionato: <b>{selectedUser ? `${selectedUser.first_name} ${selectedUser.last_name}` : '—'}</b>
          </div>
        </div>
      </div>

      <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 16 }}>
        <SummaryCard
          label="Giorni lavorativi"
          value={String(totals.workingDays)}
          sub={`${totals.holidays} festivi · ${totals.saturdaySunday} sab/dom`}
        />

        <SummaryCard
          label="Ore contratto mese"
          value={fmtHM(totals.expectedMin)}
          sub={`${fmtDecimalHours(totals.expectedMin)} ore`}
        />

        <SummaryCard
          label="Ore lavorate nette"
          value={fmtHM(totals.workedMin)}
          sub={`${fmtDecimalHours(totals.workedMin)} ore`}
        />

        <SummaryCard
          label="Ore pausa"
          value={fmtHM(totals.breakMin)}
          sub={`${fmtDecimalHours(totals.breakMin)} ore`}
        />

        <SummaryCard
          label={absenceLabel}
          value={fmtHM(totals.absenceMin)}
          sub={`${totals.absentFullDays} giorni pieni · ${totals.partialAbsenceDays} parziali`}
        />

        <SummaryCard
          label="Straordinario"
          value={fmtHM(totals.overtimeMin)}
          sub={`${fmtDecimalHours(totals.overtimeMin)} ore`}
        />
      </div>

      <div className="card" style={{ overflow: 'hidden' }}>
        <div className="spread" style={{ gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
          <div>
            <div className="h3" style={{ margin: 0 }}>
              Calendario mensile
            </div>

            <div className="small">
              Clicca un giorno per vedere il dettaglio.
            </div>
          </div>

          <div className="small">
            Contratto: <b>{contractHours.toLocaleString('it-IT')}h/giorno</b>
          </div>
        </div>

        <div
          className="grid"
          style={{
            gridTemplateColumns: 'repeat(7, minmax(140px, 1fr))',
            gap: 8,
            overflowX: 'auto'
          }}
        >
          {['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'].map((label) => (
            <div
              key={label}
              className="small"
              style={{
                padding: '8px 10px',
                fontWeight: 900,
                textAlign: 'center',
                background: 'var(--black)',
                color: 'var(--white)',
                borderRadius: 10
              }}
            >
              {label}
            </div>
          ))}

          {days.map((d) => (
            <button
              key={d.ymd}
              type="button"
              onClick={() => openDay(d)}
              disabled={!d.inMonth}
              className="card"
              style={{
                ...kindBadgeStyle(d.kind),
                minHeight: 150,
                textAlign: 'left',
                opacity: d.inMonth ? 1 : 0.35,
                cursor: d.inMonth ? 'pointer' : 'default',
                boxShadow: 'none',
                padding: 10,
                display: 'grid',
                alignContent: 'start',
                gap: 6
              }}
            >
              <div className="spread" style={{ gap: 8 }}>
                <b style={{ fontSize: 18 }}>
                  {d.date.getDate()}
                </b>

                {d.ymd === today ? (
                  <span className="badge badgeGray">
                    Oggi
                  </span>
                ) : null}
              </div>

              <div className="small">
                {kindLabel(d.kind, d.holidayName)}
              </div>

              {d.inMonth ? (
                <>
                  {d.workday ? (
                    <>
                      <div style={{ fontWeight: 900 }}>
                        Netto {fmtHM(d.workedMin)}
                      </div>

                      <div className="small">
                        Pausa {fmtHM(d.breakMin)}
                      </div>
                    </>
                  ) : d.isWorkingDay ? (
                    <div style={{ fontWeight: 900 }}>
                      Nessuna timbratura
                    </div>
                  ) : (
                    <div className="small">
                      Non lavorativo
                    </div>
                  )}

                  {d.absenceMin > 0 ? (
                    <div className="small">
                      {absenceLabel}: <b>{fmtHM(d.absenceMin)}</b>
                    </div>
                  ) : null}

                  {d.overtimeMin > 0 ? (
                    <div className="small">
                      Straordinario: <b>{fmtHM(d.overtimeMin)}</b>
                    </div>
                  ) : null}
                </>
              ) : null}
            </button>
          ))}
        </div>
      </div>

      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <div style={{ padding: 16 }}>
          <div className="h3" style={{ margin: 0 }}>
            Dettaglio giorni del mese
          </div>

          <div className="small">
            Le righe sabato, domenica e festivi non hanno ore da contratto.
            Se sono state lavorate, le ore vengono considerate straordinario.
          </div>
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'var(--black)', color: 'var(--white)' }}>
                <th style={{ padding: 12, textAlign: 'left' }}>Data</th>
                <th style={{ padding: 12, textAlign: 'left' }}>Tipo</th>
                <th style={{ padding: 12, textAlign: 'left' }}>Inizio</th>
                <th style={{ padding: 12, textAlign: 'left' }}>Fine</th>
                <th style={{ padding: 12, textAlign: 'left' }}>Netto</th>
                <th style={{ padding: 12, textAlign: 'left' }}>Pausa</th>
                <th style={{ padding: 12, textAlign: 'left' }}>{absenceLabel}</th>
                <th style={{ padding: 12, textAlign: 'left' }}>Straordinario</th>
              </tr>
            </thead>

            <tbody>
              {orderedMonthDays.map((d, idx) => (
                <tr
                  key={d.ymd}
                  style={{
                    background: idx % 2
                      ? 'rgba(255,255,255,.02)'
                      : 'rgba(255,255,255,.04)'
                  }}
                >
                  <td style={{ padding: 12 }}>
                    {fmtDateISO(d.ymd)}
                  </td>

                  <td style={{ padding: 12 }}>
                    {kindLabel(d.kind, d.holidayName)}
                  </td>

                  <td style={{ padding: 12 }}>
                    {d.workday ? fmtTime(d.workday.start_work_at) : '—'}
                  </td>

                  <td style={{ padding: 12 }}>
                    {d.workday ? fmtTime(d.workday.end_work_at) : '—'}
                  </td>

                  <td style={{ padding: 12, fontWeight: 900 }}>
                    {fmtHM(d.workedMin)}
                  </td>

                  <td style={{ padding: 12 }}>
                    {fmtHM(d.breakMin)}
                  </td>

                  <td style={{ padding: 12 }}>
                    {fmtHM(d.absenceMin)}
                  </td>

                  <td style={{ padding: 12 }}>
                    {fmtHM(d.overtimeMin)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Modal
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        title={selectedDay ? `Dettaglio giorno · ${fmtDateISO(selectedDay.ymd)}` : 'Dettaglio giorno'}
      >
        {selectedDay ? (
          <div style={{ display: 'grid', gap: 12 }}>
            <div
              className="card"
              style={{
                ...kindBadgeStyle(selectedDay.kind),
                boxShadow: 'none'
              }}
            >
              <div className="spread" style={{ gap: 10, alignItems: 'flex-start' }}>
                <div>
                  <div className="small">
                    Tipo giorno
                  </div>

                  <div style={{ fontSize: 22, fontWeight: 900 }}>
                    {kindLabel(selectedDay.kind, selectedDay.holidayName)}
                  </div>
                </div>

                <div className="badge badgeGray">
                  {selectedDay.ymd}
                </div>
              </div>
            </div>

            <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12 }}>
              <SummaryCard
                label="Ore contratto"
                value={fmtHM(selectedDay.expectedMin)}
                sub={`${fmtDecimalHours(selectedDay.expectedMin)} ore`}
              />

              <SummaryCard
                label="Ore lavorate"
                value={fmtHM(selectedDay.workedMin)}
                sub={`${fmtDecimalHours(selectedDay.workedMin)} ore`}
              />

              <SummaryCard
                label="Ore pausa"
                value={fmtHM(selectedDay.breakMin)}
                sub={`${fmtDecimalHours(selectedDay.breakMin)} ore`}
              />

              <SummaryCard
                label={absenceLabel}
                value={fmtHM(selectedDay.absenceMin)}
                sub={`${fmtDecimalHours(selectedDay.absenceMin)} ore`}
              />

              <SummaryCard
                label="Straordinario"
                value={fmtHM(selectedDay.overtimeMin)}
                sub={`${fmtDecimalHours(selectedDay.overtimeMin)} ore`}
              />
            </div>

            <div className="card" style={{ boxShadow: 'none', border: '1px solid var(--border)' }}>
              <div className="h3" style={{ marginTop: 0 }}>
                Timbratura
              </div>

              {selectedDay.workday ? (
                <div className="grid" style={{ gap: 10 }}>
                  <DetailRow label="Inizio lavoro" value={fmtTime(selectedDay.workday.start_work_at)} />
                  <DetailRow label="Inizio pausa" value={fmtTime(selectedDay.workday.start_break_at)} />
                  <DetailRow label="Fine pausa" value={fmtTime(selectedDay.workday.end_break_at)} />
                  <DetailRow label="Fine lavoro" value={fmtTime(selectedDay.workday.end_work_at)} />
                </div>
              ) : selectedDay.isWorkingDay ? (
                <div className="small">
                  Nessuna timbratura presente. Il giorno viene conteggiato come {absenceLabel.toLowerCase()} per {fmtHM(selectedDay.absenceMin)}.
                </div>
              ) : (
                <div className="small">
                  Giorno non lavorativo. Non vengono conteggiate ore da contratto.
                </div>
              )}
            </div>

            <div className="spread">
              <button
                className="btn btnSecondary"
                type="button"
                onClick={() => setDetailOpen(false)}
              >
                Chiudi
              </button>
            </div>
          </div>
        ) : null}
      </Modal>
    </div>
  );
}

function SummaryCard(props: {
  label: string;
  value: string;
  sub?: string;
}) {
  return (
    <div className="card">
      <div className="small">
        {props.label}
      </div>

      <div className="h2" style={{ margin: 0 }}>
        {props.value}
      </div>

      {props.sub ? (
        <div className="small" style={{ marginTop: 4 }}>
          {props.sub}
        </div>
      ) : null}
    </div>
  );
}

function DetailRow(props: {
  label: string;
  value: string;
}) {
  return (
    <div className="spread" style={{ gap: 12 }}>
      <div className="small">
        {props.label}
      </div>

      <b>
        {props.value}
      </b>
    </div>
  );
}